import { useState, useMemo, useEffect, useCallback } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
  virtual_tour_type?: string | null;
  virtual_tour_thumbnail?: string | null;
}

type TabKey = 'community' | 'apartment';

const TAB_LABELS: Record<TabKey, string> = {
  community: 'Tour Your New Community',
  apartment: 'Tour Your New Home',
};

/**
 * Returns true only if `value` looks like a real URL/path. Upstream feeds
 * sometimes pass literal strings like "false", "False", "null", "none",
 * etc. instead of an empty value — those should be treated as missing.
 */
function isUsableUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === 'false' || lower === 'true' || lower === 'null' || lower === 'undefined' || lower === 'none' || lower === 'n/a') {
    return false;
  }
  return true;
}

function classifyTour(tour: VirtualTour): TabKey {
  const typeVal = (tour.virtual_tour_type || '').toLowerCase();
  if (typeVal.includes('apartment')) return 'apartment';
  if (typeVal.includes('community')) return 'community';

  const title = (tour.virtual_tour_title || '').toLowerCase();
  if (title.includes('apartment') || title.includes('unit') || title.includes('home') || title.includes('floorplan'))
    return 'apartment';
  return 'community';
}

function TourRow({ tour, onOpen }: { tour: VirtualTour; onOpen: (tour: VirtualTour) => void }) {
  const hasThumbnail = isUsableUrl(tour.virtual_tour_thumbnail);

  return (
    <div className="vt-row">
      {hasThumbnail && (
        <div className="vt-row-thumb">
          <VibesitesImage url={tour.virtual_tour_thumbnail!} alt={tour.virtual_tour_title || 'Virtual Tour'} sizes="140px" className="w-full h-full object-cover" />
        </div>
      )}

      <div className="flex-1 min-w-0 py-2">
        <p className="font-body text-base sm:text-lg text-[var(--vibe-color-text)]">
          {tour.virtual_tour_title || 'Virtual Tour'}
        </p>
        <p className="font-body text-xs text-[var(--vibe-color-text-muted)] mt-1">Virtual Tour</p>
      </div>

      <button
        onClick={() => onOpen(tour)}
        className="vt-view-btn flex-shrink-0"
      >
        View Virtual Tour
      </button>
    </div>
  );
}

function TourModal({ tour, onClose }: { tour: VirtualTour; onClose: () => void }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="vt-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label={tour.virtual_tour_title || 'Virtual Tour'}>
      <div className="vt-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="vt-modal-header">
          <p className="font-body text-sm sm:text-base text-white truncate pr-4">
            {tour.virtual_tour_title || 'Virtual Tour'}
          </p>
          <button onClick={onClose} className="vt-modal-close" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Iframe */}
        <div className="vt-modal-body">
          <iframe
            src={tour.virtual_tour_url || ''}
            className="w-full h-full border-0"
            allowFullScreen
            title={tour.virtual_tour_title || 'Virtual Tour'}
          />
        </div>
      </div>
    </div>
  );
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const validTours = useMemo(
    () => (tours || []).filter((t) => isUsableUrl(t.virtual_tour_url)),
    [tours],
  );

  const grouped = useMemo(() => {
    const map: Record<TabKey, VirtualTour[]> = { community: [], apartment: [] };
    for (const tour of validTours) {
      map[classifyTour(tour)].push(tour);
    }
    return map;
  }, [validTours]);

  const availableTabs = useMemo(
    () => (['community', 'apartment'] as TabKey[]).filter((k) => grouped[k].length > 0),
    [grouped],
  );

  const [activeTab, setActiveTab] = useState<TabKey>('community');
  const [modalTour, setModalTour] = useState<VirtualTour | null>(null);

  const openModal = useCallback((tour: VirtualTour) => setModalTour(tour), []);
  const closeModal = useCallback(() => setModalTour(null), []);

  const effectiveTab = availableTabs.includes(activeTab)
    ? activeTab
    : availableTabs[0] || 'community';
  const activeTours = grouped[effectiveTab] || [];

  if (validTours.length === 0) {
    return (
      <section className="bg-white py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="aspect-video w-full bg-[var(--vibe-color-surface)] rounded-lg flex items-center justify-center">
            <p className="text-[var(--vibe-color-text-muted)] font-body">
              No virtual tours are available for this property.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white py-8 md:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Tabs — only shown when both Community and Apartment tours exist */}
        {availableTabs.length > 1 && (
          <div className="vt-tab-row">
            {availableTabs.map((key) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`vt-tab ${effectiveTab === key ? 'vt-tab--active' : ''}`}
              >
                {TAB_LABELS[key]}
              </button>
            ))}
          </div>
        )}

        {/* Tour list */}
        <div className={`vt-list ${availableTabs.length <= 1 ? 'vt-list--no-tabs' : ''}`}>
          {activeTours.map((tour, i) => (
            <TourRow key={`${effectiveTab}-${i}`} tour={tour} onOpen={openModal} />
          ))}
        </div>
      </div>

      {/* Modal */}
      {modalTour && <TourModal tour={modalTour} onClose={closeModal} />}

      <style>{`
        /* ── Tab bar ── */
        .vt-tab-row {
          display: flex;
          gap: 0;
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
          margin-bottom: 0;
        }
        .vt-tab {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 500;
          padding: 0.75rem 1.25rem;
          color: var(--vibe-color-text-muted, #6b7280);
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          transition: color 0.2s ease, border-color 0.2s ease;
          white-space: nowrap;
        }
        .vt-tab:hover {
          color: var(--vibe-color-text, #111);
        }
        .vt-tab--active {
          color: var(--vibe-color-text, #111);
          border-bottom-color: var(--vibe-color-text, #111);
          font-weight: 600;
        }

        @media (max-width: 639px) {
          .vt-tab {
            flex: 1 1 0;
            min-width: 0;
            white-space: normal;
            text-align: center;
            padding: 0.625rem 0.5rem;
            font-size: 0.8125rem;
            line-height: 1.3;
          }
        }

        /* ── Tour list ── */
        .vt-list {
          display: flex;
          flex-direction: column;
        }
        .vt-list--no-tabs {
          border-top: 1px solid var(--vibe-color-border, #e5e7eb);
        }

        /* ── Tour row ── */
        .vt-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem 0;
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
        }
        .vt-row:last-child {
          border-bottom: none;
        }

        @media (min-width: 640px) {
          .vt-row { gap: 1.5rem; }
        }

        /* ── Thumbnail ── */
        .vt-row-thumb {
          flex-shrink: 0;
          width: 140px;
          height: 90px;
          border-radius: 4px;
          overflow: hidden;
          background: var(--vibe-color-surface, #f5f5f5);
        }
        @media (max-width: 639px) {
          .vt-row-thumb {
            width: 100px;
            height: 68px;
          }
        }

        /* ── View button ── */
        .vt-view-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.625rem 1.25rem;
          font-family: var(--vibe-font-body);
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-decoration: none;
          color: var(--vibe-color-text-on-secondary);
          background: var(--vibe-color-secondary);
          border: none;
          border-radius: 9999px;
          cursor: pointer;
          transition: all 0.25s ease;
          white-space: nowrap;
        }
        .vt-view-btn:hover {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }

        @media (max-width: 639px) {
          .vt-view-btn {
            font-size: 0.6rem;
            padding: 0.5rem 0.875rem;
          }
        }

        /* ── Modal ── */
        .vt-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.75);
          animation: vtFadeIn 0.2s ease;
        }

        .vt-modal {
          position: relative;
          width: 92vw;
          max-width: 1100px;
          background: #111;
          border-radius: 8px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          animation: vtSlideUp 0.25s ease;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }

        .vt-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: #111;
          flex-shrink: 0;
        }

        .vt-modal-close {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }
        .vt-modal-close:hover {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }

        .vt-modal-body {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%; /* 16:9 aspect ratio */
        }
        .vt-modal-body iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        }

        @media (max-width: 639px) {
          .vt-modal {
            width: 100vw;
            max-width: none;
            border-radius: 0;
          }
        }

        @keyframes vtFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes vtSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as any).contentSchema = contentSchema;

export default VirtualTourEmbed;
